import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { useDefaultFilterMenuOptions, useNotesTableColumns } from "@/entities/notes-table-v3";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { formatDayJs } from "@/shared/utils";
import { createNewTempNote, type NotesManager } from "@/features/notes-manager";
import {
  DEFAULT_NOTE_TYPES,
  useDeleteNotesByIdsMutation,
  useGetPersonByIdQuery,
  useLazyGetNoteByIdQuery,
} from "@/services/api";
import { DATE_FORMAT } from "@/shared/constants";
import { useAlert } from "@/shared/hooks";
import { getErrorMessage } from "@/services/errors";
import type { MaybeType } from "@/shared/types";
import type { Key } from "react";
import type { TableRowSelection } from "antd/es/table/interface";
import type { NotesTableItem } from "../types";
import { notesContentActions, notesContentSelectors } from "../slice";
import { useTabsManager } from "./use-tabs-manager";
import { useFilters } from "./use-filters";

export const useNotesManager = (): NotesManager => {
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const { t } = useTranslation();
  const { personId } = useParams();

  const [deleteNotesByIds] = useDeleteNotesByIdsMutation();
  const [getNoteById] = useLazyGetNoteByIdQuery();

  const { data: currentPerson } = useGetPersonByIdQuery(
    { id: personId ?? "" },
    { skip: !personId || personId === "TEMP_ID" },
  );

  const { notesTableColumnsGetters } = useNotesTableColumns();
  const { getDefaultPersonGroupOptions, getDefaultNoteTypeOptions } = useDefaultFilterMenuOptions();
  const tabsManager = useTabsManager();
  const filters = useFilters();

  const dispatch = useAppDispatch();

  const { callError } = useAlert();

  const titleFilter = useAppSelector(notesContentSelectors.selectTitleFilter);
  const noteTitleFilter = useAppSelector(notesContentSelectors.selectNoteTitleFilter);
  const notesTableData = useAppSelector(
    notesContentSelectors.selectNotesTableDataByPersonId(personId),
  );
  const selectedNotes = useAppSelector(notesContentSelectors.selectSelectedNotes);
  const isLoading = useAppSelector(notesContentSelectors.selectIsLoading);
  const selectedIds = useAppSelector(notesContentSelectors.selectSelectedIds);
  const swipedNote = useAppSelector(notesContentSelectors.selectSwipedNote);
  const sorter = useAppSelector(notesContentSelectors.selectSorter);
  const stringFilter = useAppSelector(notesContentSelectors.selectStringFilter);
  const hasFilters = useAppSelector(notesContentSelectors.hasFilters);
  const fromDateFilter = useAppSelector(notesContentSelectors.selectFromDateFilter);
  const toDateFilter = useAppSelector(notesContentSelectors.selectToDateFilter);
  const personsData = useAppSelector(notesContentSelectors.selectPersons);
  const activeNote = useAppSelector(notesContentSelectors.selectActiveNote);

  const columns = useMemo(
    () => [
      notesTableColumnsGetters.getNotesTableImportantColumn({
        title: "",
        dataIndex: ["important"],
      }),
      notesTableColumnsGetters.getNotesTableNoteDateColumn({
        title: t("common.date"),
        dataIndex: ["noteDate"],
        onChangeFilterDateFrom: (date) => filters.setFromDateFilter(formatDayJs(date)),
        onChangeFilterDateTo: (date) => filters.setToDateFilter(formatDayJs(date)),
        defaultValueFilterFrom: fromDateFilter ? dayjs(fromDateFilter, DATE_FORMAT) : undefined,
        defaultValueFilterTo: toDateFilter ? dayjs(toDateFilter, DATE_FORMAT) : undefined,
      }),
      notesTableColumnsGetters.getNotesTableNoteTypeColumn({
        title: t("common.type"),
        dataIndex: ["noteType.name"],
        menuFilters: getDefaultNoteTypeOptions(),
        onChangeMenuFilters: (selected) => filters.setNoteTypeFilters(selected),
      }),
      notesTableColumnsGetters.getNotesTableNoteTitleColumn({
        dataIndex: ["title"],
        title: t("common.noteType.note_one"),
        onChangeFilter: filters.setNoteTitleFilter,
        filterValue: noteTitleFilter,
      }),
    ],
    [
      t,
      filters,
      titleFilter,
      noteTitleFilter,
      getDefaultPersonGroupOptions,
      getDefaultNoteTypeOptions,
      notesTableColumnsGetters,
    ],
  );

  const onSelectChange = useCallback(
    (selectedKeys: Key[]) => {
      dispatch(notesContentActions.setSelectedIds(selectedKeys));
    },
    [dispatch],
  );

  const rowSelection: TableRowSelection<NotesTableItem> = useMemo(
    () => ({
      selectedNotes,
      onChange: onSelectChange,
      columnWidth: 35,
    }),
    [selectedNotes, onSelectChange],
  );

  const handleClickRow = useCallback(
    (note: NotesTableItem) => {
      getNoteById({ noteId: note.id })
        .unwrap()
        .then((res) => {
          const person = personsData.find((p) => p.id === res.personId) ?? null;
          dispatch(notesContentActions.addSelectedNote({ ...res, person }));
          dispatch(notesContentActions.setActiveNote({ noteId: note.id }));
        })
        .catch((e) => {
          callError({ text: getErrorMessage(e.code) });
        });
    },
    [dispatch, getNoteById, personsData],
  );

  const handleAddTempNote = useCallback(() => {
    if (!currentPerson) return;

    const newNote = {
      ...createNewTempNote(),
      title: t("notesPage.newNote"),
      noteType: { name: DEFAULT_NOTE_TYPES.DEFAULT_NOTE },
      person: currentPerson,
      personId: currentPerson?.id ?? "",
    };

    dispatch(notesContentActions.addSelectedNote(newNote));
    dispatch(notesContentActions.setActiveNote({ noteId: newNote.id }));
  }, [dispatch, t, currentPerson]);

  const deleteSelected = useCallback(
    (ids: string[]) => {
      deleteNotesByIds(ids)
        .unwrap()
        .then(() => {
          setDeleteModalIsOpen(false);
          dispatch(notesContentActions.setSelectedNotes([]));
        })
        .catch((e) => {
          callError({ text: getErrorMessage(e.code) });
        });
    },
    [deleteNotesByIds, dispatch],
  );

  const setSelectedNotes = useCallback(
    (notes: NotesTableItem[]) => {
      dispatch(notesContentActions.setSelectedNotes(notes));
    },
    [dispatch],
  );

  const setSwipedNote = useCallback(
    (noteId: MaybeType<string>) => {
      dispatch(notesContentActions.setSwipedNote(noteId));
    },
    [dispatch],
  );

  const handleOpenModal = useCallback(() => {
    setDeleteModalIsOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setDeleteModalIsOpen(false);
  }, []);

  const selectedTableNotes = useMemo(() => {
    return notesTableData?.filter((note) => selectedIds.includes(note.id)) ?? [];
  }, [selectedIds, notesTableData]);

  return {
    state: {
      columns,
      notesTableData,
      isLoading,
      selectedTableNotes,
      deleteModalIsOpen,
      deleteDisabled: !selectedTableNotes.length,
      selectedIds,
      swipedNote,
      selectedNotes,
      sorter,
      stringFilter,
      emptyNotes: notesTableData.length === 0 && !hasFilters,
      activeNote,
    },
    methods: {
      rowSelection,
      handleClickRow,
      handleAddTempNote,
      deleteSelected,
      handleCloseModal,
      handleOpenModal,
      onSelectChange,
      upsertNote: tabsManager.methods.upsertNote,
      deleteNoteFileById: tabsManager.methods.deleteNoteFileById,
      setSelectedNotes,
      setSwipedNote,
      setStringFilter: filters.setStringFilter,
    },
    tabsManager,
    filters,
  };
};
